import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Unlock, Pencil, Calendar } from "lucide-react";
import api, { apiErr, resolveImg } from "@/lib/api";

export const EDITION_STATUS = {
  open: { label: "En cours", cls: "bg-green-100 text-green-700" },
  upcoming: { label: "À venir", cls: "bg-blue-100 text-blue-700" },
  closed: { label: "Clôturée", cls: "bg-slate-200 text-slate-600" },
};
const EMPTY = { name: "", description: "", box_ids: [], start_date: "", end_date: "" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const inputCls = "w-full border border-slate-300 rounded-xl px-4 py-2.5 outline-none focus:border-cc-red";

function EditionForm({ boxes, form, setForm, editing, busy, onSubmit, onCancel }) {
  const toggleBox = (id) => setForm((f) => ({ ...f, box_ids: f.box_ids.includes(id) ? f.box_ids.filter((b) => b !== id) : [...f.box_ids, id] }));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="card-cocarde p-5 h-fit" data-testid="edition-form">
      <h3 className="font-display text-xl cc-navy mb-3">{editing ? "Modifier l'édition" : "Nouvelle édition"}</h3>
      <div className="space-y-3">
        <input data-testid="edition-form-name" placeholder="Nom (ex : Coupe du monde 2026)" value={form.name} onChange={set("name")} className={inputCls} />
        <textarea data-testid="edition-form-description" placeholder="Description" rows={2} value={form.description} onChange={set("description")} className={inputCls} />
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs font-bold text-slate-500">Début
            <input data-testid="edition-form-start" type="date" value={form.start_date} onChange={set("start_date")} className={`${inputCls} mt-1 font-normal text-slate-800`} />
          </label>
          <label className="text-xs font-bold text-slate-500">Fin
            <input data-testid="edition-form-end" type="date" value={form.end_date} onChange={set("end_date")} className={`${inputCls} mt-1 font-normal text-slate-800`} />
          </label>
        </div>
        <p className="text-xs font-bold text-slate-500">Boîtes de l'édition</p>
        <div className="max-h-56 overflow-y-auto space-y-1 border border-slate-200 rounded-xl p-2">
          {boxes.map((b) => (
            <label key={b.id} data-testid={`edition-form-box-${b.id}`} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm">
              <input type="checkbox" checked={form.box_ids.includes(b.id)} onChange={() => toggleBox(b.id)} />
              <img src={resolveImg(b.image_url)} alt="" className="w-8 h-8 object-contain" />
              <span className="font-semibold cc-navy">{b.name}</span>
            </label>
          ))}
          {boxes.length === 0 && <p className="text-slate-400 text-sm p-2">Aucun modèle de boîte. Ajoutez-en dans « Modèles de boîtes ».</p>}
        </div>
        <button data-testid="edition-form-submit" onClick={onSubmit} disabled={busy} className="btn-cocarde w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <Plus size={16} /> {editing ? "Enregistrer" : "Créer l'édition"}
        </button>
        {editing && <button data-testid="edition-form-cancel" onClick={onCancel} className="w-full text-sm font-bold text-slate-500 hover:text-cc-red">Annuler</button>}
      </div>
    </div>
  );
}

function EditionCard({ e, onEdit, onToggle, onDelete }) {
  const st = EDITION_STATUS[e.status] || EDITION_STATUS.open;
  const isClosed = e.status === "closed";
  return (
    <div data-testid={`edition-item-${e.slug}`} className="card-cocarde p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-display text-xl cc-navy truncate">{e.name}</p>
          <p className="text-slate-500 text-xs flex items-center gap-1 mt-0.5"><Calendar size={12} /> {fmt(e.start_date)} → {fmt(e.end_date)}</p>
        </div>
        <span data-testid={`edition-status-${e.slug}`} className={`text-xs font-bold rounded-full px-2 py-0.5 shrink-0 ${st.cls}`}>{st.label}</span>
      </div>
      <p className="text-slate-600 text-sm mt-2 truncate">{e.boxes.map((b) => b.name).join(" · ") || "Aucune boîte"}</p>
      <p className="text-slate-400 text-xs mt-1">{e.candidate_count} candidat{e.candidate_count > 1 ? "s" : ""} validé{e.candidate_count > 1 ? "s" : ""}</p>
      <div className="flex gap-2 mt-3">
        <button data-testid={`edition-edit-${e.slug}`} onClick={() => onEdit(e)} className="flex-1 btn-navy text-sm py-1.5 inline-flex items-center justify-center gap-1"><Pencil size={14} /> Modifier</button>
        <button data-testid={`edition-toggle-${e.slug}`} onClick={() => onToggle(e)} className={`flex-1 rounded-full py-1.5 text-sm font-bold inline-flex items-center justify-center gap-1 text-white ${isClosed ? "bg-green-500" : "bg-cc-red"}`}>
          {isClosed ? <><Unlock size={14} /> Rouvrir</> : <><Lock size={14} /> Clôturer</>}
        </button>
        <button data-testid={`edition-delete-${e.slug}`} onClick={() => onDelete(e)} className="text-slate-400 hover:text-cc-red p-2"><Trash2 size={18} /></button>
      </div>
    </div>
  );
}

export default function EditionsManager() {
  const [editions, setEditions] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/editions").then((r) => setEditions(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/boxes").then((r) => setBoxes(r.data)); }, []);

  const reset = () => { setForm(EMPTY); setEditing(null); };
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/editions/${editing}`, form);
      else await api.post("/admin/editions", form);
      toast.success(editing ? "Édition mise à jour" : "Édition créée");
      reset(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const edit = (e) => { setEditing(e.id); setForm({ name: e.name, description: e.description || "", box_ids: e.box_ids || [], start_date: e.start_date || "", end_date: e.end_date || "" }); };
  const toggle = async (e) => {
    const closed = e.status !== "closed";
    try { await api.put(`/admin/editions/${e.id}/close`, { closed }); toast.success(closed ? "Édition clôturée" : "Édition rouverte"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const del = async (e) => {
    if (!window.confirm(`Supprimer l'édition « ${e.name} » ?`)) return;
    try { await api.delete(`/admin/editions/${e.id}`); toast.success("Édition supprimée"); if (editing === e.id) reset(); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <EditionForm boxes={boxes} form={form} setForm={setForm} editing={editing} busy={busy} onSubmit={submit} onCancel={reset} />
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4 h-fit">
        {editions.map((e) => <EditionCard key={e.id} e={e} onEdit={edit} onToggle={toggle} onDelete={del} />)}
        {editions.length === 0 && <p className="text-slate-400">Aucune édition.</p>}
      </div>
    </div>
  );
}
